import { worker } from './mocks/browser';
import ReactDOM from 'react-dom/client';
import './index.css';
import './styles/general.scss';
import { App } from './app/App';
import reportWebVitals from './reportWebVitals';
import { BrowserRouter, createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router-dom';
import { Provider } from 'react-redux';
import { store } from './app/store';
import { SnackbarProvider } from 'notistack';
import NotificationSnackBar from './components/NotificationSnackBar';
import * as Sentry from '@sentry/react';
import { useEffect } from 'react';
import { NonceProvider, useNonce } from './context/NonceContext';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { pdfjs } from 'react-pdf';

pdfjs.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString();

async function enableMocking() {
  if (process.env.NODE_ENV === 'development') {
    return worker.start();
  }
}

process.env.REACT_APP_SENTRY_DSN !== 'disabled' &&
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    tracePropagationTargets: ['localhost', String(process.env.REACT_APP_API_URL)],
  });

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

declare module 'notistack' {
  interface VariantOverrides {
    myCustomVariant: true;
    notification: {
      type?: 'success' | 'warning' | 'info' | 'error' | 'loading';
    };
  }
}

const AppWithNonce = () => {
  const nonce = useNonce();

  // Створюємо кеш для Emotion з nonce
  const cache = createCache({
    key: 'mui-style',
    nonce,
    prepend: true,
  });

  return (
    <CacheProvider value={cache}>
      <SnackbarProvider
        maxSnack={5}
        autoHideDuration={5000}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        Components={{
          notification: NotificationSnackBar,
        }}
      >
        <BrowserRouter>
          <Provider store={store}>
            <App />
          </Provider>
        </BrowserRouter>
      </SnackbarProvider>
    </CacheProvider>
  );
};

enableMocking().then(() => {
  root.render(
    <NonceProvider>
      <AppWithNonce />
    </NonceProvider>
  );
});

reportWebVitals();
